import {
  type RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Below this the card is treated as a hand-held surface. It is a content
 * threshold, not a structural one — where the line items switch between the
 * table and stacked variants is the line items' own business.
 */
const COMPACT_MAX_WIDTH = 600;

/**
 * Assume the card has room until it has been laid out, so a wide card renders
 * its full treatment on the first paint rather than flashing the narrow one.
 */
const UNMEASURED_WIDTH = Number.POSITIVE_INFINITY;

export type InvoiceCardLayout = {
  width: number;
  isCompact: boolean;
  padX: number;
  iconButtonPadding: number;
  minTouchTarget: number;
};

const layoutForWidth = (width: number): InvoiceCardLayout => {
  const isCompact = width < COMPACT_MAX_WIDTH;
  return {
    width,
    isCompact,
    padX: isCompact ? 16 : 20,
    iconButtonPadding: isCompact ? 13 : 8,
    minTouchTarget: isCompact ? 44 : 30,
  };
};

/**
 * Measures the card rather than the viewport, so a card rendered into a split
 * pane gets the stacked variant on a wide screen too. The card's own width is
 * whatever its container gives it, so a measurement can never feed back into
 * the thing being measured.
 */
export const useCardLayout = (): {
  ref: RefObject<HTMLDivElement>;
  layout: InvoiceCardLayout;
} => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(UNMEASURED_WIDTH);
  const layout = useMemo(() => layoutForWidth(width), [width]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () => setWidth(element.offsetWidth);
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      window.addEventListener("orientationchange", measure);
      return () => {
        window.removeEventListener("resize", measure);
        window.removeEventListener("orientationchange", measure);
      };
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, layout };
};
